import * as Sentry from '@sentry/react';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { SocketStatus } from '../data-models/socket-base.data-model';
import { ChatSocketMessageType, IChatMessage } from '../schemas/agent-conversation.schema';
import { getAgentWssUrl } from '../util/environment';

interface IHudsonWsResponseBaseDataModel {
  type: ChatSocketMessageType;
}

export interface IWebSocket {
  get socketStatus(): Observable<SocketStatus>;
  get socketStatusSync(): SocketStatus;
  disconnect: () => void;
  reconnect: () => Promise<void>;
  listen<Response extends IHudsonWsResponseBaseDataModel>(
    eventName: string,
    Listener: (data: Response) => void
  ): Subscription;
  send(message: IChatMessage): void;
}

const POLICY_VIOLATION_CLOSE_CODE = 1008;

export class HudsonWsService implements IWebSocket {
  static readonly #MAX_RETRY_COUNT = 5;
  static #instance: IWebSocket;

  #getToken: () => Promise<string>;
  #getTenantId: () => string | undefined;
  readonly #requestsBuffer: IChatMessage[];

  #retryCount = 0;
  #socket: WebSocket | undefined;
  #connectedToken: string | undefined;
  #socketStatus: BehaviorSubject<SocketStatus>;
  #responseChannels: Map<string, Subject<unknown>>;

  private constructor(getToken: () => Promise<string>, getTenantId: () => string | undefined) {
    this.#getToken = getToken;
    this.#getTenantId = getTenantId;
    this.#requestsBuffer = [];
    this.#responseChannels = new Map();
    this.#connect();
    this.#socketStatus = new BehaviorSubject<SocketStatus>(SocketStatus.connecting);
  }

  disconnect() {
    this.#responseChannels.forEach((value) => {
      value.complete();
      value.unsubscribe();
    });
    this.#responseChannels = new Map<string, Subject<unknown>>();
    this.#clearListeners();
    this.#socket?.close();
    this.#socketStatus.next(SocketStatus.closed);
  }

  /**
   * Reconnects the WebSocket without destroying response channels, preserving existing subscriptions.
   * Unlike disconnect(), this keeps listeners active and resets the retry count for a fresh connection attempt.
   */
  async reconnect(): Promise<void> {
    this.#clearListeners();
    this.#socket?.close();
    this.#retryCount = 0;
    this.#socketStatus.next(SocketStatus.connecting);
    await this.#connect();
  }

  listen<Response extends IHudsonWsResponseBaseDataModel>(
    eventName: string,
    listener: (data: Response) => void
  ) {
    let channel = this.#responseChannels.get(eventName) as Subject<Response>;
    if (!channel) {
      channel = new Subject<Response>();
      this.#responseChannels.set(eventName, channel as Subject<unknown>);
    }

    return channel.asObservable().subscribe(listener);
  }

  send(message: IChatMessage) {
    if (this.#socketStatus.getValue() !== SocketStatus.ready) {
      this.#requestsBuffer.push(message);
    } else {
      this.#sendMessage(message);
    }
  }

  get socketStatus() {
    return this.#socketStatus.asObservable();
  }

  get socketStatusSync() {
    return this.#socketStatus.getValue();
  }

  async #connect() {
    const token = await this.#getToken();
    this.#connectedToken = token;
    this.#socket = new WebSocket(this.#getConnectionUrl(token, this.#getTenantId()));
    this.#socket.addEventListener('open', this.#onSocketOpen);
    this.#socket.addEventListener('close', this.#onSocketClose);
    this.#socket.addEventListener('error', this.#onSocketError);
    this.#socket.addEventListener('message', this.#onSocketMessage);
  }

  #retryConnect() {
    if (this.#retryCount < HudsonWsService.#MAX_RETRY_COUNT) {
      this.#socketStatus.next(SocketStatus.connecting);
      this.#retryCount++;
      Sentry.logger.info(`[HudsonWsService] retrying connection to websocket, count ${this.#retryCount}.`);

      setTimeout(() => {
        this.#connect();
      }, this.#retryCount * 1000);
    } else {
      console.error(
        `[HudsonWsService] Failed to establish connection after ${HudsonWsService.#MAX_RETRY_COUNT} attempts.`
      );
      this.#socketStatus.next(SocketStatus.error);
    }
  }

  #clearListeners() {
    if (this.#socket) {
      this.#socket.removeEventListener('open', this.#onSocketOpen);
      this.#socket.removeEventListener('close', this.#onSocketClose);
      this.#socket.removeEventListener('error', this.#onSocketError);
      this.#socket.removeEventListener('message', this.#onSocketMessage);
    }
  }

  #flushBuffer(): void {
    this.#requestsBuffer.forEach((msg) => {
      this.#sendMessage(msg);
    });
    this.#requestsBuffer.length = 0; // Clear the buffer
  }

  // The customer tenant rides beside the token so a parent-account user is
  // routed to the customer they are working in; absent, the agent routes by
  // the token's own tenant
  #getConnectionUrl(authToken: string, tenantId?: string): string {
    const url = `${getAgentWssUrl()}?token=${authToken}`;
    return tenantId ? `${url}&tenant_id=${encodeURIComponent(tenantId)}` : url;
  }

  #onSocketClose = (closeEvent: CloseEvent) => {
    Sentry.logger.info('[WebSocketService]  Socket closed - disconnected', { closeEvent });
    this.#clearListeners();

    // 1008 is the agent refusing the token or the tenant attribute; retrying the
    // same credentials cannot succeed, so surface the error without a reconnect burst
    if (closeEvent.code === POLICY_VIOLATION_CLOSE_CODE) {
      this.#socketStatus.next(SocketStatus.error);
      return;
    }
    // See iana.org/assignments/websocket/websocket.xhtml for details.
    if (closeEvent.code > 1000 || !closeEvent.wasClean) {
      this.#retryConnect();
    } else {
      this.#socketStatus.next(SocketStatus.closed);
    }
  };

  #onSocketError = (errorEvent: Event) => {
    Sentry.logger.error('[WebSocketService] Socket error', { errorEvent });
    this.#clearListeners();
    this.#socketStatus.next(SocketStatus.error);
    this.#retryConnect();
  };

  #onSocketMessage = (event: MessageEvent) => {
    const data: unknown = this.#readMessage(event.data);

    if (!(data as IHudsonWsResponseBaseDataModel).type) {
      console.error('[WebSocketService] unexpected message', data);
      return;
    }

    this.handleGenericResponse(data as IHudsonWsResponseBaseDataModel);
  };

  private handleGenericResponse(response: IHudsonWsResponseBaseDataModel) {
    const channel = this.#responseChannels.get(response.type);
    channel?.next(response);
  }

  #onSocketOpen = () => {
    Sentry.logger.info('[WebSocketService] Socket opened - connected');
    this.#retryCount = 0;
    this.#socketStatus.next(SocketStatus.ready);
    this.#flushBuffer();
  };

  #readMessage<T>(msg: string) {
    return JSON.parse(msg) as T;
  }

  async #sendMessage(msg: IChatMessage) {
    const token = await this.#getToken();
    if (!this.#socket) {
      // should not happen
      Sentry.logger.error('Socket not initialized');
      return;
    }
    // The agent answers with the token the socket connected with, so a renewed token needs a new
    // connection. The frame waits in the buffer, which flushes when the new socket opens.
    if (token !== this.#connectedToken) {
      this.#requestsBuffer.push(msg);
      if (this.#socketStatus.getValue() !== SocketStatus.connecting) {
        // A failed reconnect would otherwise leave the status at connecting, where frames only buffer.
        this.reconnect().catch((error: unknown) => {
          Sentry.logger.error('[HudsonWsService] Reconnecting with a renewed token failed', {
            error: String(error),
          });
          this.#socketStatus.next(SocketStatus.error);
        });
      }
      return;
    }
    const tenantId = this.#getTenantId();
    this.#socket.send(JSON.stringify({ ...msg, token, ...(tenantId ? { tenant_id: tenantId } : {}) }));
  }

  static async initService(
    getToken: () => Promise<string>,
    getTenantId: () => string | undefined = () => undefined
  ): Promise<IWebSocket> {
    if (HudsonWsService.#instance && HudsonWsService.#instance.socketStatusSync !== SocketStatus.closed) {
      Sentry.logger.info('[WebSocketService] Socket has already been initialized, refreshing token getter');
      // The getter closes over the access token that was current when it was
      // created, so keep the freshest one here: reconnect() and send() must
      // authenticate with the live session, not with the token captured when
      // the socket was first constructed (e.g. before a token refresh or
      // tenant switch).
      (HudsonWsService.#instance as HudsonWsService).#getToken = getToken;
      (HudsonWsService.#instance as HudsonWsService).#getTenantId = getTenantId;
    } else {
      HudsonWsService.#instance = new HudsonWsService(getToken, getTenantId);
    }

    return HudsonWsService.#instance;
  }

  static destroyService() {
    if (HudsonWsService.#instance) {
      HudsonWsService.#instance.disconnect();
    }
  }

  static get() {
    return HudsonWsService.#instance;
  }
}

// TODO: we also need to ping the server to keep the connection alive
